import { z } from "zod";
const InterviewLanguage = {
  DANISH: "da-DK",
  DUTCH: "nl-NL",
  ENGLISH: "en-US",
  ENGLISH_AND_SPANISH: "en-US,es-ES",
  FRENCH: "fr-FR",
  FRENCH_CANADA: "fr-CA",
  GEORGIAN: "ka-GE",
  GERMAN: "de-DE",
  GREEK: "el-GR",
  HINDI: "hi-IN",
  HUNGARIAN: "hu-HU",
  ITALIAN: "it-IT",
  JAPANESE: "ja-JP",
  KANNADA: "kn-IN",
  KOREAN: "ko-KR",
  MANDARIN: "zh-CN",
  MULTILANG: "multilang",
  NORWEGIAN: "nb-NO",
  POLISH: "pl-PL",
  PORTUGUESE: "pt-PT",
  ROMANIAN: "ro-RO",
  RUSSIAN: "ru-RU",
  SPANISH: "es-ES",
  TAMIL: "ta-IN",
  TELUGU: "te-IN",
  UKRAINIAN: "uk-UA",
  VIETNAMESE: "vi-VN"
};
const allInterviewLanguages = [
  InterviewLanguage.DANISH,
  InterviewLanguage.DUTCH,
  InterviewLanguage.ENGLISH,
  InterviewLanguage.ENGLISH_AND_SPANISH,
  InterviewLanguage.FRENCH,
  InterviewLanguage.FRENCH_CANADA,
  InterviewLanguage.GEORGIAN,
  InterviewLanguage.GERMAN,
  InterviewLanguage.GREEK,
  InterviewLanguage.HINDI,
  InterviewLanguage.HUNGARIAN,
  InterviewLanguage.ITALIAN,
  InterviewLanguage.JAPANESE,
  InterviewLanguage.KANNADA,
  InterviewLanguage.KOREAN,
  InterviewLanguage.MANDARIN,
  InterviewLanguage.MULTILANG,
  InterviewLanguage.NORWEGIAN,
  InterviewLanguage.POLISH,
  InterviewLanguage.PORTUGUESE,
  InterviewLanguage.ROMANIAN,
  InterviewLanguage.RUSSIAN,
  InterviewLanguage.SPANISH,
  InterviewLanguage.TAMIL,
  InterviewLanguage.TELUGU,
  InterviewLanguage.UKRAINIAN,
  InterviewLanguage.VIETNAMESE
];
const interviewLanguageSchema = z.enum(allInterviewLanguages);
const multilangLanguages = Object.freeze([
  // Do not add languages here unless Retell's multilingual agent supports them!
  InterviewLanguage.DUTCH,
  InterviewLanguage.ENGLISH,
  InterviewLanguage.FRENCH,
  InterviewLanguage.GERMAN,
  InterviewLanguage.HINDI,
  InterviewLanguage.ITALIAN,
  InterviewLanguage.JAPANESE,
  InterviewLanguage.PORTUGUESE,
  InterviewLanguage.RUSSIAN,
  InterviewLanguage.SPANISH
]);
const allTranslationLanguages = [
  InterviewLanguage.ENGLISH,
  InterviewLanguage.GERMAN,
  InterviewLanguage.SPANISH
];
const uiTranslationLanguages = [InterviewLanguage.SPANISH];
function checkShouldShowUITranslation(language) {
  return uiTranslationLanguages.some((l) => l === language);
}
const LANGUAGE_DISPLAY_NAMES = {
  [InterviewLanguage.DANISH]: "Danish",
  [InterviewLanguage.DUTCH]: "Dutch",
  [InterviewLanguage.ENGLISH]: "English",
  [InterviewLanguage.ENGLISH_AND_SPANISH]: "English & Spanish Multilingual",
  [InterviewLanguage.FRENCH]: "French",
  [InterviewLanguage.FRENCH_CANADA]: "French (Canada)",
  [InterviewLanguage.GEORGIAN]: "Georgian",
  [InterviewLanguage.GERMAN]: "German",
  [InterviewLanguage.GREEK]: "Greek",
  [InterviewLanguage.HINDI]: "Hindi",
  [InterviewLanguage.HUNGARIAN]: "Hungarian",
  [InterviewLanguage.ITALIAN]: "Italian",
  [InterviewLanguage.JAPANESE]: "Japanese",
  [InterviewLanguage.KANNADA]: "Kannada",
  [InterviewLanguage.KOREAN]: "Korean",
  [InterviewLanguage.MANDARIN]: "Chinese (Mandarin)",
  [InterviewLanguage.MULTILANG]: "Multilingual",
  [InterviewLanguage.NORWEGIAN]: "Norwegian",
  [InterviewLanguage.POLISH]: "Polish",
  [InterviewLanguage.PORTUGUESE]: "Portuguese",
  [InterviewLanguage.ROMANIAN]: "Romanian",
  [InterviewLanguage.RUSSIAN]: "Russian",
  [InterviewLanguage.SPANISH]: "Spanish",
  [InterviewLanguage.TAMIL]: "Tamil",
  [InterviewLanguage.TELUGU]: "Telugu",
  [InterviewLanguage.UKRAINIAN]: "Ukrainian",
  [InterviewLanguage.VIETNAMESE]: "Vietnamese"
};
const LANGUAGE_TO_FLAG = {
  [InterviewLanguage.DANISH]: "\u{1F1E9}\u{1F1F0}",
  [InterviewLanguage.DUTCH]: "\u{1F1F3}\u{1F1F1}",
  [InterviewLanguage.ENGLISH]: "\u{1F1FA}\u{1F1F8}",
  [InterviewLanguage.ENGLISH_AND_SPANISH]: "\u{1F1FA}\u{1F1F8}\u{1F1EA}\u{1F1F8}",
  [InterviewLanguage.FRENCH]: "\u{1F1EB}\u{1F1F7}",
  [InterviewLanguage.FRENCH_CANADA]: "\u{1F1EB}\u{1F1F7}\u{1F1E8}\u{1F1E6}",
  [InterviewLanguage.GEORGIAN]: "\u{1F1EC}\u{1F1EA}",
  [InterviewLanguage.GERMAN]: "\u{1F1E9}\u{1F1EA}",
  [InterviewLanguage.GREEK]: "\u{1F1EC}\u{1F1F7}",
  [InterviewLanguage.HINDI]: "\u{1F1EE}\u{1F1F3}",
  [InterviewLanguage.HUNGARIAN]: "\u{1F1ED}\u{1F1FA}",
  [InterviewLanguage.ITALIAN]: "\u{1F1EE}\u{1F1F9}",
  [InterviewLanguage.JAPANESE]: "\u{1F1EF}\u{1F1F5}",
  [InterviewLanguage.KANNADA]: "\u{1F1EE}\u{1F1F3}",
  [InterviewLanguage.KOREAN]: "\u{1F1F0}\u{1F1F7}",
  [InterviewLanguage.MANDARIN]: "\u{1F1E8}\u{1F1F3}",
  [InterviewLanguage.MULTILANG]: "\u{1F30D}",
  [InterviewLanguage.NORWEGIAN]: "\u{1F1F3}\u{1F1F4}",
  [InterviewLanguage.POLISH]: "\u{1F1F5}\u{1F1F1}",
  [InterviewLanguage.PORTUGUESE]: "\u{1F1F5}\u{1F1F9}",
  [InterviewLanguage.ROMANIAN]: "\u{1F1F7}\u{1F1F4}",
  [InterviewLanguage.RUSSIAN]: "\u{1F1F7}\u{1F1FA}",
  [InterviewLanguage.SPANISH]: "\u{1F1EA}\u{1F1F8}",
  [InterviewLanguage.TAMIL]: "\u{1F1EE}\u{1F1F3}",
  [InterviewLanguage.TELUGU]: "\u{1F1EE}\u{1F1F3}",
  [InterviewLanguage.UKRAINIAN]: "\u{1F1FA}\u{1F1E6}",
  [InterviewLanguage.VIETNAMESE]: "\u{1F1FB}\u{1F1F3}"
};
function getAgentLanguageBaseInstructions(language, multilangLanguages2) {
  if (language === InterviewLanguage.ENGLISH_AND_SPANISH) {
    return "Respond in English or Spanish, whichever the candidate is speaking.";
  }
  if (language === InterviewLanguage.MULTILANG) {
    if (!multilangLanguages2 || multilangLanguages2.length === 0) {
      return "Always respond in English.";
    }
    const languageList = multilangLanguages2.map((language2) => LANGUAGE_DISPLAY_NAMES[language2]).join(" or ");
    return `Always respond in ${languageList}, whichever the candidate is speaking.`;
  }
  return `Always respond in ${LANGUAGE_DISPLAY_NAMES[language]}.`;
}
const LANGUAGES_WITH_GENDERED_FIRST_PERSON = /* @__PURE__ */ new Set([
  InterviewLanguage.HINDI,
  InterviewLanguage.FRENCH,
  InterviewLanguage.FRENCH_CANADA,
  InterviewLanguage.ITALIAN,
  InterviewLanguage.POLISH,
  InterviewLanguage.PORTUGUESE,
  InterviewLanguage.ROMANIAN,
  InterviewLanguage.RUSSIAN,
  InterviewLanguage.SPANISH,
  InterviewLanguage.GERMAN,
  InterviewLanguage.GEORGIAN,
  InterviewLanguage.GREEK,
  InterviewLanguage.UKRAINIAN
]);
function getAgentLanguageInstructions(language, multilangLanguages2, agentGender) {
  const baseInstruction = getAgentLanguageBaseInstructions(
    language,
    multilangLanguages2
  );
  const languagesToCheck = language === InterviewLanguage.MULTILANG && multilangLanguages2 ? multilangLanguages2 : [language];
  const hasGenderedLanguage = languagesToCheck.some(
    (lang) => LANGUAGES_WITH_GENDERED_FIRST_PERSON.has(lang)
  );
  if (hasGenderedLanguage && agentGender) {
    const genderInstruction = agentGender === "feminine" ? "Use feminine grammatical forms in first person (matching the voice synthesis), but if asked directly about your identity, clarify that you're an AI assistant without human gender or identity." : "Use masculine grammatical forms in first person (matching the voice synthesis), but if asked directly about your identity, clarify that you're an AI assistant without human gender or identity.";
    return `${baseInstruction} ${genderInstruction}`;
  }
  return baseInstruction;
}
const EnglishVoices = {
  American: "american",
  British: "british",
  Filipino: "filipino",
  Indian: "indian",
  SouthAfrican: "south_african"
};
const allEnglishVoices = [
  EnglishVoices.American,
  EnglishVoices.British,
  EnglishVoices.Filipino,
  EnglishVoices.Indian,
  EnglishVoices.SouthAfrican
];
const SpanishVoices = {
  Default: "spanish_default",
  Spain: "spanish_spain",
  Mexico: "spanish_mexico"
};
const allSpanishVoices = [
  SpanishVoices.Default,
  SpanishVoices.Spain,
  SpanishVoices.Mexico
];
function getVoicesForLanguage(language) {
  switch (language) {
    case InterviewLanguage.ENGLISH:
      return allEnglishVoices;
    case InterviewLanguage.SPANISH:
      return allSpanishVoices;
    default:
      return [];
  }
}
function getDefaultVoiceForLanguage(language) {
  switch (language) {
    case InterviewLanguage.ENGLISH:
      return EnglishVoices.American;
    case InterviewLanguage.SPANISH:
      return SpanishVoices.Default;
    default:
      return null;
  }
}
const languageVoiceDisplayNames = {
  [EnglishVoices.American]: "American accent (female)",
  [EnglishVoices.British]: "British accent (female)",
  [EnglishVoices.Filipino]: "Filipino accent (male)",
  [EnglishVoices.Indian]: "Indian accent (female)",
  [EnglishVoices.SouthAfrican]: "South African accent (female)",
  [SpanishVoices.Default]: "Default",
  [SpanishVoices.Spain]: "Spain (female)",
  [SpanishVoices.Mexico]: "Mexico (female)"
};
const FEMININE_AGENT_PERSONA = {
  name: "Anna",
  gender: "feminine"
};
const MASCULINE_AGENT_PERSONA = {
  name: "Daniel",
  gender: "masculine"
};
const MASCULINE_VOICE_LANGUAGES = /* @__PURE__ */ new Set([
  InterviewLanguage.UKRAINIAN
]);
function getAgentPersona(language, voice) {
  if (voice === EnglishVoices.Filipino) {
    return MASCULINE_AGENT_PERSONA;
  }
  if (MASCULINE_VOICE_LANGUAGES.has(language)) {
    return MASCULINE_AGENT_PERSONA;
  }
  return FEMININE_AGENT_PERSONA;
}
const languageTags = {
  [InterviewLanguage.DANISH]: { default: ["da-DK"] },
  [InterviewLanguage.DUTCH]: { default: ["nl-NL"] },
  [InterviewLanguage.ENGLISH]: {
    default: ["en-US"],
    [EnglishVoices.American]: ["en-US"],
    [EnglishVoices.British]: ["en-GB"],
    [EnglishVoices.Filipino]: ["en-PH"],
    [EnglishVoices.Indian]: ["en-IN"],
    [EnglishVoices.SouthAfrican]: ["en-ZA"]
  },
  [InterviewLanguage.ENGLISH_AND_SPANISH]: { default: ["en-US", "es-ES"] },
  [InterviewLanguage.FRENCH]: { default: ["fr-FR"] },
  [InterviewLanguage.FRENCH_CANADA]: { default: ["fr-CA"] },
  [InterviewLanguage.GEORGIAN]: { default: ["ka-GE"] },
  [InterviewLanguage.GERMAN]: { default: ["de-DE"] },
  [InterviewLanguage.GREEK]: { default: ["el-GR"] },
  [InterviewLanguage.HINDI]: { default: ["hi-IN"] },
  [InterviewLanguage.HUNGARIAN]: { default: ["hu-HU"] },
  [InterviewLanguage.ITALIAN]: { default: ["it-IT"] },
  [InterviewLanguage.JAPANESE]: { default: ["ja-JP"] },
  [InterviewLanguage.KANNADA]: { default: ["kn-IN"] },
  [InterviewLanguage.KOREAN]: { default: ["ko-KR"] },
  [InterviewLanguage.MANDARIN]: { default: ["zh-CN"] },
  [InterviewLanguage.MULTILANG]: {
    // Based on `multilangLanguages`
    default: [
      "da-DK",
      "nl-NL",
      "en-US",
      "fr-FR",
      "de-DE",
      "hi-IN",
      "it-IT",
      "ja-JP",
      "pt-PT",
      "ru-RU",
      "es-ES"
    ]
  },
  [InterviewLanguage.NORWEGIAN]: { default: ["nb-NO"] },
  [InterviewLanguage.POLISH]: { default: ["pl-PL"] },
  [InterviewLanguage.PORTUGUESE]: { default: ["pt-PT"] },
  [InterviewLanguage.ROMANIAN]: { default: ["ro-RO"] },
  [InterviewLanguage.RUSSIAN]: { default: ["ru-RU"] },
  [InterviewLanguage.SPANISH]: { default: ["es-ES"] },
  [InterviewLanguage.TAMIL]: { default: ["ta-IN"] },
  [InterviewLanguage.TELUGU]: { default: ["te-IN"] },
  [InterviewLanguage.UKRAINIAN]: { default: ["uk-UA"] },
  [InterviewLanguage.VIETNAMESE]: { default: ["vi-VN"] }
};
function getLanguageTagsForProjectSettings(settings) {
  const { language, languageVoice: voice } = settings;
  const englishTags = languageTags[InterviewLanguage.ENGLISH];
  if (language === InterviewLanguage.ENGLISH && voice !== null && voice in englishTags) {
    return Array.from(englishTags[voice]);
  }
  const { multilangLanguages: multilangLanguages2 } = settings;
  if (language === InterviewLanguage.MULTILANG && multilangLanguages2 !== null && multilangLanguages2.length > 0) {
    return multilangLanguages2.flatMap((l) => languageTags[l].default);
  }
  return Array.from(languageTags[language].default);
}
const RETELL_SUPPORTED_LANGUAGES = Object.freeze([
  InterviewLanguage.DANISH,
  InterviewLanguage.DUTCH,
  InterviewLanguage.ENGLISH,
  InterviewLanguage.ENGLISH_AND_SPANISH,
  InterviewLanguage.FRENCH,
  InterviewLanguage.FRENCH_CANADA,
  InterviewLanguage.GERMAN,
  InterviewLanguage.GREEK,
  InterviewLanguage.HINDI,
  InterviewLanguage.HUNGARIAN,
  InterviewLanguage.ITALIAN,
  InterviewLanguage.JAPANESE,
  InterviewLanguage.KOREAN,
  InterviewLanguage.MANDARIN,
  InterviewLanguage.MULTILANG,
  InterviewLanguage.NORWEGIAN,
  InterviewLanguage.POLISH,
  InterviewLanguage.PORTUGUESE,
  InterviewLanguage.ROMANIAN,
  InterviewLanguage.RUSSIAN,
  InterviewLanguage.SPANISH,
  InterviewLanguage.UKRAINIAN,
  InterviewLanguage.VIETNAMESE
]);
function checkIsRetellSupportedLanguage(language) {
  return RETELL_SUPPORTED_LANGUAGES.some((l) => l === language);
}
const LIVEKIT_UNSUPPORTED_LANGUAGES = Object.freeze([
  InterviewLanguage.ENGLISH_AND_SPANISH,
  InterviewLanguage.MULTILANG
]);
function checkIsLivekitSupportedLanguage(language) {
  return !LIVEKIT_UNSUPPORTED_LANGUAGES.some((l) => l === language);
}
export {
  EnglishVoices,
  InterviewLanguage,
  LANGUAGE_DISPLAY_NAMES,
  LANGUAGE_TO_FLAG,
  SpanishVoices,
  allInterviewLanguages,
  allTranslationLanguages,
  checkIsLivekitSupportedLanguage,
  checkIsRetellSupportedLanguage,
  checkShouldShowUITranslation,
  getAgentLanguageBaseInstructions,
  getAgentLanguageInstructions,
  getAgentPersona,
  getDefaultVoiceForLanguage,
  getLanguageTagsForProjectSettings,
  getVoicesForLanguage,
  interviewLanguageSchema,
  languageVoiceDisplayNames,
  multilangLanguages,
  uiTranslationLanguages
};
//# sourceMappingURL=languages.js.map
